import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { useForm } from "@tellescope/forms";
import { useCallback, useMemo, useState } from "react";
import { ComboboxOption, useDebouncedValue } from "@tellescope/ui";
import { addMinutes } from "date-fns/addMinutes";
import { useTranslation } from "react-i18next";
import { useUnavailableUsersGuard } from "./use-unavailable-users-guard";

export interface EventFormInitialValues {
  title?: string;
  templateId?: string;
  description?: string;
  color?: string;
  locationIds?: string[];
  startDate?: Date;
  duration?: number;
  userIds?: string[];
  enduserIds?: string[];
  enableVideoCall?: boolean;
  hiddenFromPortal?: boolean;
  publicRead?: boolean;
  dontBlockAvailability?: boolean;
  locationName?: string;
  locationURL?: string;
  locationPhone?: string;
  locationNotes?: string;
  actualDuration?: number;
  internalNotes?: string;
  reason?: string;
  zoomIntegration?: boolean;
  generateZoomLinkOnCreate?: boolean;
  dontAutoSyncPatientToHealthie?: boolean;
}

export interface UseEventFormDialogContents {
  eventId?: string;
  onSuccess: () => void;
  initialFormValues?: EventFormInitialValues;
  timezone?: string;
}

export const useEventFormDialogContents = ({
  eventId,
  onSuccess,
  initialFormValues,
  timezone,
}: UseEventFormDialogContents) => {
  const { t } = useTranslation("calendar");
  const { selectedOrganization } = useAuth();
  const isEditing = !!eventId;
  const { guard: guardUpdate, warningDialogProps: updateWarningDialogProps } = useUnavailableUsersGuard({ timezone });
  const updateEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "updateOne",
    onSuccess,
  });
  const createEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "createOne",
    onSuccess,
  });
  const generateZoomMeetingMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "generate_zoom_meeting",
  });

  const eventQuery = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: eventId ?? "",
    enabled: !!eventId,
  });

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });

  const { data: users = [] } = useUsersQuery();
  const [enduserSearch, setEnduserSearch] = useState("");
  const [debouncedEnduserSearch] = useDebouncedValue(enduserSearch);
  const [selectedTab, setSelectedTab] = useState(t("Info"));
  const tabsList = [
    { label: t("Info") },
    { label: t("Location"), isRequired: selectedOrganization?.settings?.calendar?.locationRequired },
    { label: t("Notes") },
    { label: t("Reminders") },
    { label: t("Integrations") },
  ];
  const [_reminderState, setReminders] = useState(eventQuery.data?.reminders);
  const reminders = useMemo(
    () => _reminderState ?? eventQuery.data?.reminders,
    [_reminderState, eventQuery.data?.reminders]
  );

  const form = useForm({
    fields: {
      title: {
        type: "text",
        initialValue: eventQuery.data?.title ?? initialFormValues?.title ?? "",
      },
      templateId: {
        type: "select",
        initialValue: eventQuery.data?.templateId ?? initialFormValues?.templateId ?? "",
        required: selectedOrganization?.settings?.calendar?.templateRequired,
      },
      description: {
        type: "textarea",
        initialValue: eventQuery.data?.description ?? initialFormValues?.description ?? "",
      },
      color: {
        type: "color",
        initialValue: eventQuery.data?.color ?? initialFormValues?.color ?? "",
      },
      locationIds: {
        type: "multiselect",
        initialValue: eventQuery.data
          ? Array.from(
              new Set<string>(
                [eventQuery.data.locationId ?? "", ...(eventQuery.data.locationIds ?? [])].filter(Boolean)
              )
            )
          : initialFormValues?.locationIds ?? [],
        dependencies: ["locationName"],
      },
      startDate: {
        type: "dateTime",
        initialValue: eventQuery.data?.startTimeInMS
          ? new Date(eventQuery.data.startTimeInMS)
          : initialFormValues?.startDate,
      },
      duration: {
        type: "number",
        initialValue: eventQuery.data?.durationInMinutes ?? initialFormValues?.duration ?? 30,
      },
      userIds: {
        type: "multiselect",
        initialValue:
          eventQuery.data?.attendees.reduce<string[]>((acc, curr) => {
            if (curr.type === "user") {
              acc.push(curr.id);
            }
            return acc;
          }, []) ??
          initialFormValues?.userIds ??
          [],
        dependencies: ["enduserIds"],
      },
      enduserIds: {
        type: "multiselect",
        initialValue:
          eventQuery.data?.attendees.reduce<string[]>((acc, curr) => {
            if (curr.type === "enduser") {
              acc.push(curr.id);
            }
            return acc;
          }, []) ??
          initialFormValues?.enduserIds ??
          [],
        dependencies: ["userIds"],
      },
      enableVideoCall: {
        type: "checkbox",
        initialValue: eventQuery.data?.enableVideoCall ?? initialFormValues?.enableVideoCall ?? false,
      },
      hiddenFromPortal: {
        type: "checkbox",
        initialValue: eventQuery.data?.hiddenFromPortal ?? initialFormValues?.hiddenFromPortal ?? false,
      },
      publicRead: {
        type: "checkbox",
        initialValue: eventQuery.data?.publicRead ?? initialFormValues?.publicRead ?? false,
      },
      dontBlockAvailability: {
        type: "checkbox",
        initialValue: eventQuery.data?.dontBlockAvailability ?? initialFormValues?.dontBlockAvailability ?? false,
      },
      locationName: {
        type: "text",
        initialValue: eventQuery.data?.location ?? initialFormValues?.locationName ?? "",
      },
      locationURL: {
        type: "url",
        initialValue: eventQuery.data?.locationURL ?? initialFormValues?.locationURL ?? "",
      },
      locationPhone: {
        type: "phone",
        initialValue: eventQuery.data?.phone ?? initialFormValues?.locationPhone ?? "",
      },
      locationNotes: {
        type: "textarea",
        initialValue: eventQuery.data?.locationNotes ?? initialFormValues?.locationNotes ?? "",
      },
      actualDuration: {
        type: "number",
        initialValue: eventQuery.data?.actualDuration ?? initialFormValues?.actualDuration,
      },
      internalNotes: {
        type: "textarea",
        initialValue: eventQuery.data?.internalNotes ?? initialFormValues?.internalNotes ?? "",
      },
      reason: {
        type: "textarea",
        initialValue: eventQuery.data?.reason ?? initialFormValues?.reason ?? "",
      },
      zoomIntegration: {
        type: "checkbox",
        initialValue: eventQuery.data
          ? eventQuery.data.videoIntegration === "Zoom"
          : initialFormValues?.zoomIntegration ?? false,
      },
      generateZoomLinkOnCreate: {
        type: "checkbox",
        initialValue: initialFormValues?.generateZoomLinkOnCreate ?? false,
      },
      dontAutoSyncPatientToHealthie: {
        type: "checkbox",
        initialValue:
          eventQuery.data?.dontAutoSyncPatientToHealthie ?? initialFormValues?.dontAutoSyncPatientToHealthie,
      },
    },
    validators: {
      title: (value) => {
        if (!value || value.trim() === "") return t("Title is required (max 250 characters).");
        if (value.length > 250) return t("Title is required (max 250 characters).");
        return undefined;
      },
      startDate: (value) => {
        if (!value) return t("A valid start time is required.");
        const ms = value.getTime();
        if (isNaN(ms) || ms < 0 || ms > 1e13) return t("A valid start time is required.");
        return undefined;
      },
      duration: (value) => {
        if (value === undefined || value === null || isNaN(value)) {
          return t("Duration must be a positive number of minutes.");
        }
        if (value < 0 || value > 1e13) return t("Duration must be a positive number of minutes.");
        return undefined;
      },
      locationIds: (value, allValues) => {
        if (
          selectedOrganization?.settings?.calendar?.locationRequired &&
          value.length === 0 &&
          !allValues.locationName.trim()
        ) {
          return t("A location is required.");
        }
        return undefined;
      },
      userIds: (value, allValues) => {
        if (value.length + allValues.enduserIds.length === 0) {
          return t("At least one attendee is required.");
        }
        return undefined;
      },
      enduserIds: (value, allValues) => {
        if (value.length + allValues.userIds.length === 0) {
          return t("At least one attendee is required.");
        }
        return undefined;
      },
      description: (value) =>
        value && value.length > 5000 ? t("Description must be at most 5000 characters.") : undefined,
      reason: (value) => (value && value.length > 5000 ? t("Reason must be at most 5000 characters.") : undefined),
      locationNotes: (value) =>
        value && value.length > 5000 ? t("Location notes must be at most 5000 characters.") : undefined,
      internalNotes: (value) =>
        value && value.length > 5000 ? t("Internal notes must be at most 5000 characters.") : undefined,
      color: (value) => (value && value.length > 1000 ? t("Color must be at most 1000 characters.") : undefined),
      locationName: (value) =>
        value && value.length > 1000 ? t("Location must be at most 1000 characters.") : undefined,
      locationURL: (value) =>
        value && value.length > 1000 ? t("Location URL must be at most 1000 characters.") : undefined,
      locationPhone: (value) => (value && value.length > 100 ? t("Phone must be at most 100 characters.") : undefined),
      actualDuration: (value) => {
        if (value === undefined || value === null) return undefined;
        if (typeof value !== "number" || isNaN(value)) return t("Actual duration must be a number.");
        if (value < 0 || value > 1e13) return t("Actual duration must be a non-negative number.");
        return undefined;
      },
    },
    validateOn: "blur",
    sideEffects: {
      enableVideoCall: (val, { publicRead }) => {
        return {
          publicRead: val ? publicRead ?? false : false,
        };
      },
    },
    onSubmit: () => {
      onSubmit();
    },
  });

  const submitEvent = useCallback(async () => {
    const title = form.values.title;
    const startDate = form.values.startDate;
    const duration = form.values.duration;
    if (!title || !startDate || duration === undefined) return;

    const startTimeInMS = startDate.getTime();
    const userId = form.values.userIds[0];
    const videoIntegration: "Zoom" | undefined = form.values.zoomIntegration ? "Zoom" : undefined;
    const shouldGenerateZoom = !!form.values.generateZoomLinkOnCreate && !!form.values.zoomIntegration && !!userId;

    const sharedFields = {
      title,
      templateId: form.values.templateId,
      description: form.values.description,
      color: form.values.color,
      locationIds: form.values.locationIds,
      location: !form.values.locationIds.length ? form.values.locationName : "",
      locationURL: !form.values.locationIds.length ? form.values.locationURL : "",
      phone: !form.values.locationIds.length ? form.values.locationPhone : "",
      locationNotes: !form.values.locationIds.length ? form.values.locationNotes : "",
      startTimeInMS,
      durationInMinutes: duration,
      attendees: [
        ...form.values.userIds.map((id) => ({ id, type: "user" as const })),
        ...form.values.enduserIds.map((id) => ({ id, type: "enduser" as const })),
      ],
      enableVideoCall: form.values.enableVideoCall,
      hiddenFromPortal: form.values.hiddenFromPortal,
      publicRead: form.values.publicRead,
      dontBlockAvailability: form.values.dontBlockAvailability,
      actualDuration: form.values.actualDuration,
      internalNotes: form.values.internalNotes,
      reason: form.values.reason,
      videoIntegration,
      dontAutoSyncPatientToHealthie: form.values.dontAutoSyncPatientToHealthie,
      reminders,
    };

    if (eventId) {
      const id = eventId;
      let videoHostUserId: string | undefined;
      let videoURL: string | undefined;

      if (shouldGenerateZoom && userId) {
        const { updatedEvent } = await generateZoomMeetingMutation.mutateAsync({
          calendarEventId: id,
          userId,
          startTimeInMS,
          durationInMinutes: duration,
        });
        videoHostUserId = updatedEvent.videoHostUserId;
        videoURL = updatedEvent.videoURL;
      }

      await updateEventMutation.mutateAsync({
        id,
        ...sharedFields,
        videoHostUserId,
        videoURL,
        _options: {
          replaceObjectFields: true,
        },
      });
      return;
    }

    const created = await createEventMutation.mutateAsync(sharedFields);

    if (shouldGenerateZoom && userId) {
      await generateZoomMeetingMutation.mutateAsync({
        calendarEventId: created.id,
        userId,
        startTimeInMS,
        durationInMinutes: duration,
      });
    }
  }, [eventId, form, generateZoomMeetingMutation, reminders, updateEventMutation, createEventMutation]);

  const onSubmit = useCallback(() => {
    const startDate = form.values.startDate;
    const duration = form.values.duration;
    if (eventId && startDate && duration !== undefined) {
      guardUpdate({ userIds: form.values.userIds, start: startDate, durationInMinutes: duration }, () => {
        submitEvent();
      });
      return;
    }
    submitEvent();
  }, [eventId, form, guardUpdate, submitEvent]);

  const { data: selectedEndusers } = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: form.values.enduserIds,
    },
    enabled: !!form.values.enduserIds.length,
  });
  const { data: endusers = [] } = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      search: debouncedEnduserSearch
        ? {
            query: debouncedEnduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });
  const allEndusers = useMemo(() => {
    if (debouncedEnduserSearch) {
      return endusers;
    }

    const ids = new Set([...form.values.enduserIds, ...endusers.map(({ id }) => id)]);
    const result = Array.from(ids).reduce<TellescopeModelFromName<"endusers">[]>((acc, id) => {
      const match =
        endusers.find((enduser) => enduser.id === id) ?? selectedEndusers?.matches.find((enduser) => enduser.id === id);
      if (match) {
        acc.push(match);
      }
      return acc;
    }, []);
    return result;
  }, [debouncedEnduserSearch, endusers, form.values.enduserIds, selectedEndusers?.matches]);

  const calculatedEndDate = useMemo(() => {
    const startDate = form.values.startDate;
    const duration = form.values.duration;
    if (!startDate || duration === undefined) return undefined;
    return addMinutes(startDate, duration);
  }, [form.values.startDate, form.values.duration]);

  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data]);
  const templateOptions = useMemo<ComboboxOption[]>(
    () => templates.map((template) => ({ value: template.id, label: template.displayTitle || template.title })),
    [templates]
  );

  const locationsQuery = useTellescopeInfiniteQuery({
    model: "appointment_locations",
    method: "getSome",
    request: {},
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });

  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const locationOptions = useMemo<ComboboxOption[]>(
    () => locations.map((location) => ({ value: location.id, label: location.title })),
    [locations]
  );

  return {
    isEditing,
    form,
    templates,
    templateOptions,
    locations,
    locationOptions,
    calculatedEndDate,
    users,
    endusers: allEndusers,
    enduserSearch,
    setEnduserSearch,
    selectedTab,
    setSelectedTab,
    tabsList,
    event: eventQuery.data,
    reminders,
    setReminders,
    onSubmit,
    updateWarningDialogProps,
  };
};
